/**
 * GAUCHO-I18N-1 — Copy Gaucho, etiquetas y UX (ES).
 */

export const gauchoEs = {
  gauchoDailyBrief: {
    status: {
      blocked: "Bloqueado",
      attention_needed: "Requiere atención",
      insufficient_evidence: "Evidencia insuficiente",
      normal: "Normal",
    },
    headline: {
      blocked:
        "Brief de hoy: riego u otras decisiones críticas bloqueadas por falta de evidencia.",
      attention_needed: "Brief de hoy: esta parcela requiere atención.",
      insufficient_evidence:
        "Brief de hoy: evidencia insuficiente para priorizar acciones.",
      normal: "Brief de hoy: sin riesgos críticos detectados.",
    },
    summary: {
      blocked:
        "Una o más decisiones agronómicas están bloqueadas porque falta evidencia o hay contradicciones. Revisá los ítems bloqueados, cargá la evidencia faltante y validá en campo antes de actuar. No se ejecuta nada automáticamente.",
      attention_needed:
        "Hay riesgos activos o prioridades abiertas que requieren revisión humana hoy. Confirmá las observaciones, validá en campo y actualizá el cuaderno de campo. No se ejecuta nada automáticamente.",
      insufficient_evidence:
        "No hay evidencia reciente suficiente sobre esta parcela para generar prioridades confiables. Cargá observaciones de campo, registros de riego y análisis de suelo/foliar para mejorar el brief.",
      normal:
        "No se detectan riesgos críticos ni decisiones bloqueadas con la evidencia actual. Continuá con observación y documentación de campo de rutina.",
    },
    risks: {
      fire: { summary: "Puntos calientes satelitales cerca de la parcela — revisá fuentes oficiales de emergencia." },
      frost: { summary: "Pronóstico de bajas temperaturas — validá localmente antes de cualquier decisión de protección." },
      pest_disease: { summary: "Posible presión de plaga o enfermedad — se requiere monitoreo en campo." },
      water_stress: { summary: "Señal de estrés hídrico detectada — validá en campo antes de cualquier decisión de riego." },
      alerts: { summary: "Alertas activas de la parcela pendientes de revisión humana." },
    },
    priorities: {
      generic: {
        title: "Prioridad pendiente de revisión",
        desc: "Requiere revisión humana. No se ejecuta automáticamente.",
      },
    },
    tasks: {
      generic: {
        title: "Tarea sugerida pendiente de revisión",
        desc: "Tarea sugerida. Requiere revisión humana. No se ejecuta automáticamente.",
      },
    },
    blockedDecisions: {
      irrigation_need: { reason: "Necesidad de riego bloqueada — falta evidencia o hay contradicciones." },
      irrigation_amount_by_zone: { reason: "Lámina de riego por zona bloqueada — no se cumplen los gates FAO-56." },
      irrigation_timing: { reason: "Momento de riego bloqueado — falta pronóstico o evidencia de campo." },
      scouting_priority_by_zone: { reason: "Prioridad de monitoreo bloqueada — se requiere validación en campo." },
      fertilization_recommendation_n: { reason: "Recomendación de nitrógeno bloqueada — falta evidencia de suelo/foliar o del ciclo del cultivo." },
      fertilization_recommendation_pk: { reason: "Recomendación de P y K bloqueada — falta análisis de suelo." },
      vra_export_readiness: { reason: "Exportación VRA bloqueada — falta evidencia requerida." },
      report_generation_requirement: { reason: "Generación de informe bloqueada — falta evidencia de respaldo." },
      compliance_evidence_check: { reason: "Verificación de cumplimiento bloqueada — registros incompletos." },
      lifecycle_planning_questions: { reason: "Planificación de ciclo bloqueada — falta confirmación del productor." },
      frost_protection_prep: { reason: "Preparación ante helada bloqueada — se requiere validación local." },
      drought_outlook: { reason: "Perspectiva de sequía bloqueada — falta pronóstico o evidencia de campo." },
    },
  },

  gauchoPriorities: {
    fire: {
      review_official_sources: {
        title: "Revisar fuentes oficiales de incendio",
        desc: "Se detectaron puntos calientes satelitales cerca de la parcela. Contrastá con fuentes oficiales de emergencia (112, Protección Civil, agencia nacional de incendios) antes de cualquier decisión. No confirma incendio activo.",
      },
    },
    frost: {
      validate_local: {
        title: "Validar riesgo de helada localmente",
        desc: "Pronóstico de bajas temperaturas reportado. Comparalo con tu sensor o estación más cercana antes de decidir cualquier medida de protección.",
      },
    },
    pest: {
      scout_symptoms: {
        title: "Monitorear síntomas de plaga o enfermedad",
        desc: "El monitoreo en campo es obligatorio para confirmar cualquier señal de plaga o enfermedad. No confirma diagnóstico y no recomienda ningún producto, principio activo ni dosis.",
      },
    },
    waterStress: {
      validate: {
        title: "Validar señal de estrés hídrico en campo",
        desc: "La señal satelital o del modelo sugiere posible estrés hídrico. Validá en campo y revisá el registro de riego antes de considerar cualquier decisión de riego.",
      },
    },
    alerts: {
      review_general: {
        title: "Revisar alertas activas de la parcela",
        desc: "Hay una o más alertas activas en esta parcela. Abrí el Centro de Alertas, validá la evidencia y decidí los próximos pasos manualmente.",
      },
    },
    ndvi: {
      inspect_low_vigor: {
        title: "Inspeccionar zona de bajo vigor NDVI",
        desc: "Se detectó una zona de bajo vigor. Se requiere validación en campo antes de inferir causas. El NDVI por sí solo no justifica cambios de riego, fertilización ni protección de cultivos.",
      },
    },
    soilFoliar: {
      soil_lab_analysis: {
        title: "Cargar análisis de suelo reciente",
        desc: "Falta un análisis de suelo reciente. Cargá el último informe para mejorar la documentación. No genera recomendación de producto ni dosis.",
      },
      foliar_analysis: {
        title: "Cargar análisis foliar reciente",
        desc: "Falta un análisis foliar reciente. Cargá el último informe para mejorar la documentación. No genera recomendación de producto ni dosis.",
      },
    },
    irrigation: {
      irrigation_log: {
        title: "Confirmar riego reciente",
        desc: "No hay eventos de riego documentados recientes para esta parcela. Confirmá qué se aplicó (o que no se aplicó riego) en el cuaderno de campo. Los gates FAO-56 no se relajan.",
      },
      no_irrigation_confirmation: {
        title: "Confirmar ausencia de riego",
        desc: "Confirmá si la parcela no recibió riego en la ventana actual. Esta confirmación es necesaria para desbloquear la decisión de riego.",
      },
    },
    forecast: {
      review_7d: {
        title: "Revisar pronóstico a 7 días",
        desc: "El pronóstico a 7 días falta o está desactualizado. Actualizalo antes de considerar el momento o la lámina de riego. El pronóstico no es certeza.",
      },
    },
    lifecycle: {
      harvest_status: {
        title: "Confirmar estado de cosecha",
        desc: "Confirmá si el cultivo principal ya fue cosechado. Es necesario para decisiones dependientes del ciclo.",
      },
      next_crop: {
        title: "Declarar próximo cultivo planificado",
        desc: "Indicá el próximo cultivo planificado para esta parcela. Necesario para la planificación de rotación. No se ejecuta siembra ni plantación automáticamente.",
      },
      post_harvest_objective: {
        title: "Declarar objetivo post-cosecha",
        desc: "Declará tu objetivo principal post-cosecha (fijación de nitrógeno, control de erosión, estructura del suelo, etc.). Necesario para la planificación de rotación.",
      },
    },
    producer: {
      confirm_harvest_status: {
        title: "Confirmación del productor: estado de cosecha",
        desc: "Respondé la pregunta obligatoria sobre el estado de cosecha.",
      },
      declare_next_crop: {
        title: "Confirmación del productor: próximo cultivo",
        desc: "Respondé la pregunta obligatoria sobre el próximo cultivo.",
      },
      declare_post_harvest_objective: {
        title: "Confirmación del productor: objetivo post-cosecha",
        desc: "Respondé la pregunta obligatoria sobre el objetivo post-cosecha.",
      },
      confirm_no_irrigation: {
        title: "Confirmación del productor: sin riego",
        desc: "Confirmá si la parcela no recibió riego en la ventana actual.",
      },
      confirm_field_scouting_done: {
        title: "Confirmación del productor: monitoreo realizado",
        desc: "Confirmá si el monitoreo de campo fue realizado.",
      },
    },
    rotation: {
      document_inputs: {
        title: "Completar entradas de planificación de rotación",
        desc: "Las entradas de planificación de rotación están incompletas. Proporcioná la información faltante (cosecha, próximo cultivo, objetivo) para habilitar la guía de rotación. No recomienda especies ni mezclas específicas de cultivos de cobertura.",
      },
    },
    docs: {
      document_observations: {
        title: "Documentar observaciones de campo de rutina",
        desc: "No hay ítems urgentes hoy. Seguí documentando observaciones de rutina en el cuaderno de campo para mantener la trazabilidad.",
      },
    },
    blockedDecisions: {
      irrigation_need: {
        title: "Necesidad de riego bloqueada",
        desc: "La necesidad de riego no puede cuantificarse con la evidencia actual. Revisá la evidencia faltante y validá en campo. No se ejecuta riego automáticamente.",
      },
      irrigation_amount_by_zone: {
        title: "Lámina de riego bloqueada",
        desc: "La lámina por zona no puede calcularse porque no se cumplen los gates FAO-56. Los gates FAO-56 nunca se relajan.",
      },
      irrigation_timing: {
        title: "Momento de riego bloqueado",
        desc: "El momento de riego no puede recomendarse sin pronóstico a 7 días actualizado y sin registro de riego aplicado.",
      },
      scouting_priority_by_zone: {
        title: "Prioridad de monitoreo bloqueada",
        desc: "La prioridad de monitoreo requiere validación en campo antes de finalizarse.",
      },
      fertilization_recommendation_n: {
        title: "Recomendación de nitrógeno bloqueada",
        desc: "La recomendación de nitrógeno está bloqueada pendiente de análisis de suelo/foliar y confirmación de ciclo. Requiere revisión agronómica calificada.",
      },
      fertilization_recommendation_pk: {
        title: "Recomendación de P y K bloqueada",
        desc: "La recomendación de P y K está bloqueada pendiente de análisis de suelo reciente. Requiere revisión agronómica calificada.",
      },
      vra_export_readiness: {
        title: "Exportación VRA bloqueada",
        desc: "La exportación VRA está bloqueada por falta de datos de zona o evidencia.",
      },
      report_generation_requirement: {
        title: "Generación de informe bloqueada",
        desc: "La generación de informe está bloqueada porque la evidencia de respaldo está incompleta.",
      },
      compliance_evidence_check: {
        title: "Verificación de cumplimiento bloqueada",
        desc: "La verificación de cumplimiento está bloqueada. No concluye estado de cumplimiento.",
      },
      lifecycle_planning_questions: {
        title: "Planificación de ciclo bloqueada",
        desc: "La planificación de ciclo está bloqueada pendiente de confirmación del productor.",
      },
      frost_protection_prep: {
        title: "Preparación ante helada bloqueada",
        desc: "La preparación ante helada está bloqueada pendiente de validación local. No activa maquinaria, calefactores, máquinas de viento ni riego como protección antihelada.",
      },
      drought_outlook: {
        title: "Perspectiva de sequía bloqueada",
        desc: "La perspectiva de sequía está bloqueada pendiente de pronóstico y evidencia de campo actualizados.",
      },
    },
  },

  gauchoTasks: {
    fire: {
      review_official_sources: {
        title: "Revisar fuentes oficiales de incendio para esta parcela",
        desc: "Contrastá los puntos calientes satelitales con fuentes oficiales de emergencia. No confirma incendio activo. No reemplaza al 112 ni a Protección Civil.",
      },
    },
    frost: {
      validate_local: {
        title: "Validar pronóstico de helada con observación local",
        desc: "Compará el pronóstico de helada con tu sensor o estación más cercana. El pronóstico no es certeza.",
      },
    },
    pest: {
      scout_symptoms: {
        title: "Monitorear síntomas de plaga o enfermedad en campo",
        desc: "Recorré la parcela y registrá los síntomas en el cuaderno de campo. No confirma diagnóstico. No recomienda ningún producto, principio activo ni dosis.",
      },
    },
    ndvi: {
      validate_low_vigor: {
        title: "Validar zona de bajo vigor NDVI en campo",
        desc: "Inspeccioná la zona de bajo vigor en campo. No inferir causa solo del NDVI.",
      },
      inspect_low_vigor: {
        title: "Inspeccionar zona de bajo vigor NDVI",
        desc: "Visitá la zona de bajo vigor y documentá lo que observás. Por sí solo no justifica cambios de riego, fertilización ni protección de cultivos.",
      },
    },
    alerts: {
      review_general: {
        title: "Revisar alertas activas de la parcela",
        desc: "Abrí el Centro de Alertas, revisá cada alerta y decidí los próximos pasos manualmente.",
      },
    },
    soilFoliar: {
      upload_soil_lab_analysis: {
        title: "Cargar análisis de suelo reciente",
        desc: "Cargá el último análisis de suelo para mantener la documentación actualizada. No recomienda ningún fertilizante ni dosis.",
      },
      upload_foliar_analysis: {
        title: "Cargar análisis foliar reciente",
        desc: "Cargá el último análisis foliar para mantener la documentación actualizada. No recomienda ningún fertilizante ni dosis.",
      },
    },
    irrigation: {
      confirm_applied: {
        title: "Confirmar riego aplicado reciente",
        desc: "Registrá qué se aplicó (o que no se aplicó riego) en el cuaderno de campo. Los gates FAO-56 nunca se relajan.",
      },
    },
    forecast: {
      review_7d: {
        title: "Revisar pronóstico a 7 días",
        desc: "Actualizá el pronóstico a 7 días antes de considerar cualquier decisión relacionada con riego.",
      },
    },
    lifecycle: {
      confirm_harvest_status: {
        title: "Confirmar estado de cosecha",
        desc: "Confirmá si el cultivo principal ya fue cosechado.",
      },
      confirm_next_crop: {
        title: "Confirmar próximo cultivo planificado",
        desc: "Confirmá el próximo cultivo planificado para la planificación de rotación.",
      },
      confirm_post_harvest_objective: {
        title: "Confirmar objetivo post-cosecha",
        desc: "Confirmá el objetivo principal post-cosecha de esta parcela.",
      },
    },
    producer: {
      confirm_harvest_status: {
        title: "Responder pregunta de estado de cosecha",
        desc: "Se requiere respuesta estructurada del productor.",
      },
      declare_next_crop: {
        title: "Responder pregunta de próximo cultivo",
        desc: "Se requiere respuesta estructurada del productor.",
      },
      declare_post_harvest_objective: {
        title: "Responder pregunta de objetivo post-cosecha",
        desc: "Se requiere respuesta estructurada del productor.",
      },
      confirm_no_irrigation: {
        title: "Responder pregunta de ausencia de riego",
        desc: "Se requiere respuesta estructurada del productor.",
      },
      confirm_field_scouting_done: {
        title: "Responder pregunta de estado de monitoreo",
        desc: "Se requiere respuesta estructurada del productor.",
      },
    },
    rotation: {
      document_inputs: {
        title: "Documentar entradas de planificación de rotación",
        desc: "Completá las entradas requeridas para la planificación de rotación. No recomienda especies ni mezclas específicas de cultivos de cobertura.",
      },
    },
    docs: {
      document_observations: {
        title: "Documentar observaciones de campo de rutina",
        desc: "Registrá observaciones de rutina en el cuaderno de campo para mantener la trazabilidad.",
      },
    },
    reports: {
      review_compliance_evidence: {
        title: "Revisar evidencia de cumplimiento",
        desc: "Abrí el Centro de Informes y revisá la evidencia de cumplimiento y auditoría disponible. No presenta reclamaciones de seguros, legales ni PAC.",
      },
    },
    water: {
      validate_context: {
        title: "Validar contexto hídrico en campo",
        desc: "Contrastá la humedad del suelo satelital con una observación de campo. El contexto hídrico es informativo — nunca activa la ejecución de riego.",
      },
    },
  },

  gauchoMemory: {
    confirmation: {
      generic: "Confirmación de campo reciente registrada.",
      no_irrigation: "Confirmación de ausencia de riego registrada.",
      irrigation_applied: "Confirmación de riego aplicado registrada.",
      harvest_status: "Confirmación de estado de cosecha registrada.",
      next_crop: "Confirmación de próximo cultivo registrada.",
      post_harvest_objective: "Confirmación de objetivo post-cosecha registrada.",
      field_scouting: "Confirmación de monitoreo de campo registrada.",
      no_irrigation_confirmed: "Confirmación de ausencia de riego registrada.",
      irrigation_uncertain: "Estado de riego incierto — la decisión permanece bloqueada.",
      lifecycle_uncertain: "Estado del ciclo incierto — la decisión permanece bloqueada.",
      crop_active_confirmed: "Confirmación de cultivo activo registrada.",
      near_harvest_confirmed: "Confirmación de proximidad a cosecha registrada.",
      harvested_confirmed: "Confirmación de cosecha registrada.",
      next_crop_declared: "Declaración de próximo cultivo registrada.",
      post_harvest_objective_declared: "Declaración de objetivo post-cosecha registrada.",
      field_validation_completed: "Confirmación de validación de campo registrada.",
      scouting_completed: "Confirmación de monitoreo completado registrada.",
    },
    fieldActivity: {
      generic: "Actividad de campo registrada.",
      irrigation: "Actividad de riego registrada.",
      scouting: "Actividad de monitoreo registrada.",
      fertilization: "Actividad de fertilización registrada.",
      observation: "Observación de campo registrada.",
      soil_sampling: "Muestreo de suelo registrado.",
      foliar_sampling: "Muestreo foliar registrado.",
    },
    irrigation: {
      recorded: "Evento de riego aplicado registrado — solo contexto histórico.",
    },
    scouting: {
      generic: "Registro de monitoreo — solo contexto histórico.",
      pest: "Registro de monitoreo de plaga — solo contexto histórico. No confirma plaga actual.",
      disease: "Registro de monitoreo de enfermedad — solo contexto histórico. No confirma enfermedad actual.",
      weed: "Registro de monitoreo de malezas — solo contexto histórico.",
    },
    soil: {
      recorded: "Evidencia de suelo registrada — solo contexto histórico. No implica producto ni dosis.",
    },
    foliar: {
      recorded: "Evidencia foliar registrada — solo contexto histórico. No implica producto ni dosis.",
    },
    season: {
      planting: {
        generic: "Evento histórico de siembra registrado.",
      },
      harvest: {
        generic: "Evento histórico de cosecha registrado.",
      },
    },
    risk: {
      historicalFireAlert: "Alerta histórica de incendio — no indica emergencia actual.",
      historicalFrostPrediction: "Predicción histórica de helada — no confirma daño por helada.",
      historicalAlert: {
        generic: "Alerta histórica — no indica condición actual.",
      },
      historicalNdviZonification: "Zonificación NDVI histórica — no infiere causa actual.",
    },
    report: {
      generic: "Registro histórico de informe — no concluye cumplimiento, seguro ni responsabilidad.",
    },
  },

  gauchoAutonomy: {
    sectionTitle: "Límites de autonomía",
    sectionSubtitle:
      "Gaucho razona, prioriza y crea tareas sugeridas seguras basadas en evidencia. No ejecuta acciones agronómicas críticas, ni maquinaria, riego, químicos, emergencias, legales, seguros ni cumplimiento.",
    activeRange:
      "Autonomía activa: niveles L1–L3. L4 está disponible únicamente como preparación y revisión supervisada, sin ejecución real. L5 permanece deshabilitado.",
    badge: {
      evidenceGated: "Basado en evidencia",
      humanReview: "Requiere revisión humana",
      criticalBlocked: "Acciones críticas bloqueadas",
    },
    module: {
      parcel_status: "Estado de la parcela",
      fao56_irrigation: "Evidencia FAO-56 de riego",
      applied_irrigation_log: "Registro de riego aplicado",
      ndvi_zones: "Zonas de vigor NDVI",
      sar_contradiction: "Contradicción SAR",
      fire_risk: "Riesgo de incendio",
      general_alerts: "Alertas generales",
      frost_risk: "Riesgo de helada",
      pest_disease: "Plagas y enfermedades",
      soil_foliar: "Evidencia de suelo / foliar",
      crop_lifecycle: "Ciclo del cultivo",
      crop_rotation: "Rotación de cultivos",
      reports: "Informes y documentación",
      daily_brief: "Brief de hoy",
      memory: "Memoria de la parcela",
      safe_tasks: "Tareas sugeridas",
      ranked_priorities: "Prioridades ordenadas",
      vegetation_indices: "Índices de vegetación",
      drought_water_stress: "Sequía y estrés hídrico",
      phenology_intelligence: "Inteligencia fenológica",
      foliar_analysis: "Análisis foliar",
      soil_analysis: "Análisis de suelo",
    },
    blocked: {
      invalid_input: "Bloqueado: entrada inválida.",
      forbidden_capability: "Bloqueado: capacidad prohibida. Nunca permitida.",
      unknown_module: "Bloqueado: módulo desconocido. Fail-closed.",
      module_blocked_capability: "Bloqueado: capacidad bloqueada por política del módulo.",
      capability_not_declared: "Bloqueado: capacidad no declarada como permitida para este módulo.",
      level5_globally_disabled: "Bloqueado: el Nivel 5 (acción autónoma directa) está deshabilitado globalmente.",
      level4_future_only: "Bloqueado: el Nivel 4 (flujo aprobado por humanos) es solo futuro.",
      exceeds_current_level: "Bloqueado: excede el nivel de autonomía activo.",
    },
    allowed: {
      within_module_policy: "Permitido dentro de la política del módulo. Requiere revisión humana.",
    },
  },
};

export default gauchoEs;